// MasterBackup.js - Complete Database Backup & Restore Dashboard  
import React, { useState, useRef } from 'react';
import {
  createMasterBackup,
  downloadBackup,
  parseBackupFile,
  previewRestore,
  restoreFromBackup,
  getBackupStats,
  compareBackups
} from '../utils/masterBackupSystem';
import './MasterBackup.css'; // Import CSS

const MasterBackup = () => {
  // State management
  const [isCreatingBackup, setIsCreatingBackup] = useState(false);
  const [backupHistory, setBackupHistory] = useState([]);
  const [selectedBackup, setSelectedBackup] = useState(null);
  const [restorePreview, setRestorePreview] = useState(null);
  const [isRestoring, setIsRestoring] = useState(false);
  const [activeTab, setActiveTab] = useState('create'); // create, restore, inspector, compare, history
  const [inspectorData, setInspectorData] = useState(null); // NEW: Inspector analysis
  const [inspectorLoading, setInspectorLoading] = useState(false); // NEW: Inspector loading state
  
  const fileInputRef = useRef(null);
  const inspectorFileRef = useRef(null); // NEW: Inspector file input

  // Create and download backup
  const handleCreateBackup = async () => {
    setIsCreatingBackup(true);
    try {
      const result = await createMasterBackup();
      
      if (result.success) {
        // Auto-download backup
        const downloadResult = downloadBackup(result.data);
        
        if (downloadResult.success) {
          // Add to history
          const backupRecord = {
            id: Date.now(),
            timestamp: result.stats.timestamp,
            filename: downloadResult.filename,
            stats: result.stats,
            data: result.data // Store for compare functionality
          };
          
          setBackupHistory(prev => [backupRecord, ...prev]);
          
          alert(`✅ Backup succesvol aangemaakt!\n\n📊 Statistieken:\n- ${result.stats.totalCollections} collections\n- ${result.stats.totalDocuments} documenten\n- ${(result.stats.backupSize / 1024 / 1024).toFixed(2)} MB\n\n💾 Backup gedownload als: ${downloadResult.filename}`);
        } else {
          alert('❌ Download mislukt: ' + downloadResult.error);
        }
      } else {
        alert('❌ Backup mislukt: ' + result.error);
      }
    } catch (error) {
      console.error('Backup error:', error);
      alert('❌ Backup fout: ' + error.message);
    } finally {
      setIsCreatingBackup(false);
    }
  };

  // Handle file upload for inspector
  const handleInspectorUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setInspectorLoading(true);
    try {
      const result = await parseBackupFile(file);
      
      if (result.success) {
        // Analyze backup for inspector
        const analysis = analyzeBackupStructure(result.data);
        
        setInspectorData({
          filename: file.name,
          backupData: result.data,
          stats: result.stats,
          analysis: analysis
        });
        
        alert(`✅ Backup geanalyseerd!\n\n📊 Gevonden:\n- ${analysis.totalCollections} collections\n- ${analysis.totalDocuments} documenten\n- ${analysis.hasSubcollections ? 'Subcollections aanwezig' : 'Geen subcollections'}\n\nBekijk de Inspector tab voor details.`);
      } else {
        alert('❌ Bestand analyseren mislukt: ' + result.error);
      }
    } catch (error) {
      alert('❌ Inspector fout: ' + error.message);
    } finally {
      setInspectorLoading(false);
    }
    
    // Reset file input
    event.target.value = '';
  };

  // Analyze backup structure for inspector
  const analyzeBackupStructure = (backupData) => {
    const analysis = {
      totalCollections: 0,
      totalDocuments: 0,
      hasSubcollections: false,
      collections: {},
      dataIntegrity: {},
      recommendations: []
    };

    for (const [collectionName, collectionData] of Object.entries(backupData.collections)) {
      const collectionAnalysis = {
        documentCount: Object.keys(collectionData).length,
        subcollections: {},
        fields: new Set(),
        sampleData: [],
        issues: []
      };

      // Analyze documents
      const documents = Object.entries(collectionData);
      documents.forEach(([docId, docData], index) => {
        // Collect field names
        Object.keys(docData).forEach(field => {
          if (field !== '_subcollections') {
            collectionAnalysis.fields.add(field);
          }
        });

        // Sample data (first 3 documents)
        if (index < 3) {
          collectionAnalysis.sampleData.push({
            id: docId,
            preview: getDocumentPreview(docData)
          });
        }

        // Check for subcollections
        if (docData._subcollections) {
          analysis.hasSubcollections = true;
          Object.entries(docData._subcollections).forEach(([subName, subData]) => {
            if (!collectionAnalysis.subcollections[subName]) {
              collectionAnalysis.subcollections[subName] = 0;
            }
            collectionAnalysis.subcollections[subName] += Object.keys(subData).length;
          });
        }
      });

      // Collection-specific validations
      collectionAnalysis.issues = validateCollectionIntegrity(collectionName, collectionData);
      
      analysis.collections[collectionName] = collectionAnalysis;
      analysis.totalDocuments += collectionAnalysis.documentCount;
    }

    analysis.totalCollections = Object.keys(backupData.collections).length;
    
    // Generate recommendations
    analysis.recommendations = generateBackupRecommendations(analysis);
    
    return analysis;
  };

  // Get document preview for inspector
  const getDocumentPreview = (docData) => {
    const preview = {};
    const importantFields = ['name', 'title', 'email', 'teamName', 'status', 'role'];
    
    // Try to get meaningful preview fields
    importantFields.forEach(field => {
      if (docData[field]) {
        preview[field] = docData[field];
      }
    });
    
    // If no important fields, get first few fields
    if (Object.keys(preview).length === 0) {
      Object.keys(docData).slice(0, 3).forEach(key => {
        if (key !== '_subcollections') {
          preview[key] = typeof docData[key] === 'string' ? 
            (docData[key].length > 50 ? docData[key].substring(0, 47) + '...' : docData[key]) :
            docData[key];
        }
      });
    }
    
    return preview;
  };

  // Validate collection integrity
  const validateCollectionIntegrity = (collectionName, collectionData) => {
    const issues = [];
    
    if (collectionName === 'tredes') {
      // Check if tredes have werkwijze field (new format)
      Object.entries(collectionData).forEach(([tredeId, tredeData]) => {
        if (tredeData.werkvormen) {
          const hasOldFormat = tredeData.werkvormen.some(w => w.steps && !w.werkwijze);
          const hasNewFormat = tredeData.werkvormen.some(w => w.werkwijze);
          
          if (hasOldFormat && !hasNewFormat) {
            issues.push(`${tredeId}: Oude werkvormen format (steps array) gevonden`);
          }
          if (hasNewFormat && hasOldFormat) {
            issues.push(`${tredeId}: Mixed werkvormen formats gevonden`);
          }
        }
      });
    }
    
    if (collectionName === 'users') {
      // Check for package fields
      const documents = Object.values(collectionData);
      const withPackages = documents.filter(doc => doc.packageScansTotal).length;
      if (withPackages > 0) {
        issues.push(`${withPackages}/${documents.length} users hebben package informatie`);
      }
    }
    
    if (collectionName === 'teams') {
      // Check for team series fields (progress tracking)
      const documents = Object.values(collectionData);
      const withSeries = documents.filter(doc => doc.teamSeriesId).length;
      if (withSeries > 0) {
        issues.push(`${withSeries}/${documents.length} teams hebben progress tracking`);
      }
    }
    
    return issues;
  };

  // Generate backup recommendations
  const generateBackupRecommendations = (analysis) => {
    const recommendations = [];
    
    // Check for essential collections
    const essentialCollections = ['teams', 'users', 'responses', 'tredes'];
    const missing = essentialCollections.filter(col => !analysis.collections[col]);
    if (missing.length > 0) {
      recommendations.push({
        type: 'warning',
        message: `Ontbrekende essential collections: ${missing.join(', ')}`
      });
    }
    
    // Check data volume
    if (analysis.totalDocuments < 10) {
      recommendations.push({
        type: 'info',
        message: 'Kleine dataset - mogelijk test environment backup'
      });
    }
    
    if (analysis.totalDocuments > 1000) {
      recommendations.push({
        type: 'success',
        message: 'Grote dataset - productie environment backup'
      });
    }
    
    // Check for werkvormen format
    const tredesIssues = analysis.collections.tredes?.issues || [];
    if (tredesIssues.some(issue => issue.includes('Oude werkvormen format'))) {
      recommendations.push({
        type: 'warning',
        message: 'Werkvormen migration nodig - run migratie voordat je dit backup gebruikt'
      });
    }
    
    return recommendations;
  };
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const result = await parseBackupFile(file);
      
      if (result.success) {
        setSelectedBackup({
          filename: file.name,
          ...result
        });
        
        // Auto-generate preview
        const preview = await previewRestore(result.data);
        setRestorePreview(preview);
        
        alert(`✅ Backup bestand geladen!\n\n📋 Inhoud:\n- Datum: ${new Date(result.stats.backupDate).toLocaleString()}\n- Collections: ${result.stats.collections.join(', ')}\n- Documenten: ${result.stats.totalDocuments}\n\n🔍 Preview gegenereerd - controleer de wijzigingen voordat je herstelt.`);
      } else {
        alert('❌ Bestand lezen mislukt: ' + result.error);
      }
    } catch (error) {
      alert('❌ Bestand fout: ' + error.message);
    }
    
    // Reset file input
    event.target.value = '';
  };

  // Execute restore
  const handleRestore = async (deleteExisting = true) => {
    if (!selectedBackup) {
      alert('❌ Geen backup bestand geselecteerd');
      return;
    }

    const confirmMessage = deleteExisting 
      ? `🚨 WAARSCHUWING: VOLLEDIGE DATABASE RESTORE\n\nDit zal:\n- Alle huidige data OVERSCHRIJVEN\n- ${restorePreview?.summary?.totalChanges || 'Vele'} wijzigingen maken\n- ${restorePreview?.summary?.collectionsAffected || 'Alle'} collections beïnvloeden\n\nWeet je dit ZEKER?\n\nTyp "RESTORE" om te bevestigen:`
      : `📝 DATABASE MERGE\n\nDit zal:\n- Backup data toevoegen/updaten\n- Bestaande data BEHOUDEN\n- ${restorePreview?.summary?.totalChanges || 'Vele'} wijzigingen maken\n\nDoorgaan?`;

    // eslint-disable-next-line no-restricted-globals
    const confirmation = prompt(confirmMessage);
    
    if (deleteExisting && confirmation !== 'RESTORE') {
      alert('❌ Restore geannuleerd - veiligheidscontrole gefaald');
      return;
    }
    
    if (!deleteExisting && !confirmation) {
      return;
    }

    setIsRestoring(true);
    try {
      const result = await restoreFromBackup(selectedBackup.data, {
        deleteExisting,
        batchSize: 500
      });

      if (result.success) {
        alert(`🎉 DATABASE RESTORE COMPLEET!\n\n📊 Resultaten:\n- ${result.totalRestored} documenten hersteld\n- ${Object.keys(result.results).length} collections bijgewerkt\n- Voltooid: ${new Date(result.timestamp).toLocaleString()}\n\n✅ Database is succesvol hersteld naar backup staat!`);
        
        // Clear current selection
        setSelectedBackup(null);
        setRestorePreview(null);
      } else {
        alert('❌ Restore mislukt: ' + result.error);
      }
    } catch (error) {
      alert('❌ Restore fout: ' + error.message);
    } finally {
      setIsRestoring(false);
    }
  };

  // Tab navigation
  const renderTabNavigation = () => (
    <div style={{
      borderBottom: '1px solid #e5e7eb',
      marginBottom: '30px'
    }}>
      <div style={{ display: 'flex', gap: '0' }}>
        {[
          { id: 'create', label: '📸 Backup Maken', icon: '💾' },
          { id: 'restore', label: '🔄 Herstellen', icon: '⚡' },
          { id: 'inspector', label: '🔍 Inspector', icon: '🔬' },
          { id: 'compare', label: '📊 Vergelijken', icon: '📈' },
          { id: 'history', label: '📚 Geschiedenis', icon: '🕐' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              background: activeTab === tab.id ? '#3b82f6' : 'transparent',
              color: activeTab === tab.id ? 'white' : '#6b7280',
              border: 'none',
              padding: '15px 25px',
              cursor: 'pointer',
              fontWeight: '500',
              fontSize: '14px',
              transition: 'all 0.2s ease'
            }}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>
    </div>
  );

  // Create Backup Tab
  const renderCreateTab = () => (
    <div>
      <div style={{
        background: '#f0f9ff',
        border: '1px solid #3b82f6',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '30px'
      }}>
        <h4 style={{ color: '#1e40af', margin: '0 0 15px 0' }}>
          📸 Dagelijkse Database Snapshot
        </h4>
        <p style={{ color: '#1e40af', margin: '0 0 15px 0', fontSize: '14px' }}>
          Maak een complete backup van alle RelatieScan data. Dit bevat alle teams, gebruikers, quiz antwoorden, werkvormen en configuratie.
        </p>
        <ul style={{ color: '#1e40af', margin: 0, paddingLeft: '20px', fontSize: '13px' }}>
          <li>Alle database collections worden geëxporteerd</li>
          <li>Subcollections (zoals team invites) zijn inbegrepen</li>
          <li>Backup wordt automatisch gedownload als JSON bestand</li>
          <li>Gebruik dagelijks voor maximale data veiligheid</li>
        </ul>
      </div>

      <div style={{ textAlign: 'center' }}>
        <button
          onClick={handleCreateBackup}
          disabled={isCreatingBackup}
          style={{
            background: isCreatingBackup ? '#9ca3af' : '#10b981',
            color: 'white',
            border: 'none',
            padding: '20px 40px',
            borderRadius: '12px',
            cursor: isCreatingBackup ? 'not-allowed' : 'pointer',
            fontWeight: '700',
            fontSize: '18px',
            boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
            transition: 'all 0.2s ease',
            minWidth: '300px'
          }}
          onMouseEnter={(e) => {
            if (!isCreatingBackup) {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.4)';
            }
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
          }}
        >
          {isCreatingBackup ? '⏳ Backup wordt aangemaakt...' : '📸 Maak Complete Database Backup'}
        </button>
        
        {isCreatingBackup && (
          <div style={{ 
            marginTop: '20px', 
            color: '#6b7280', 
            fontSize: '14px',
            fontStyle: 'italic' 
          }}>
            Even geduld, alle data wordt verzameld en ingepakt...
          </div>
        )}
      </div>

      {/* Backup Statistics */}
      <div style={{
        marginTop: '40px',
        background: '#f9fafb',
        borderRadius: '8px',
        padding: '20px'
      }}>
        <h4 style={{ margin: '0 0 15px 0', color: '#1f2937' }}>
          📊 Wat wordt er gebackupt?
        </h4>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '15px',
          fontSize: '13px'
        }}>
          <div>
            <strong>👥 Teams & Users:</strong><br/>
            Team configuraties, gebruikersprofielen, uitnodigingen
          </div>
          <div>
            <strong>📝 Quiz Data:</strong><br/>
            Alle antwoorden, scores, individuele resultaten
          </div>
          <div>
            <strong>🎯 Content:</strong><br/>
            Vragen, werkvormen, admin configuratie
          </div>
          <div>
            <strong>📧 Email History:</strong><br/>
            Verstuurde emails, uitnodigingscodes
          </div>
        </div>
      </div>
    </div>
  );

  // Restore Tab
  const renderRestoreTab = () => (
    <div>
      <div style={{
        background: '#fef3c7',
        border: '1px solid #f59e0b',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '30px'
      }}>
        <h4 style={{ color: '#92400e', margin: '0 0 15px 0' }}>
          ⚠️ Database Restore - Gebruik met voorzichtigheid!
        </h4>
        <p style={{ color: '#92400e', margin: '0', fontSize: '14px' }}>
          Upload een backup bestand om de database te herstellen. Dit kan alle huidige data overschrijven!
        </p>
      </div>

      {/* File Upload */}
      <div style={{
        border: '2px dashed #d1d5db',
        borderRadius: '8px',
        padding: '40px',
        textAlign: 'center',
        marginBottom: '30px',
        background: '#f9fafb'
      }}>
        <input
          ref={fileInputRef}
          type="file"
          accept=".json"
          onChange={handleFileUpload}
          style={{ display: 'none' }}
        />
        
        <div style={{ fontSize: '48px', marginBottom: '15px' }}>📁</div>
        
        <h4 style={{ margin: '0 0 10px 0', color: '#1f2937' }}>
          Upload Backup Bestand
        </h4>
        
        <p style={{ color: '#6b7280', margin: '0 0 20px 0', fontSize: '14px' }}>
          Selecteer een RelatieScan backup JSON bestand om te herstellen
        </p>
        
        <button
          onClick={() => fileInputRef.current?.click()}
          style={{
            background: '#3b82f6',
            color: 'white',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: '600'
          }}
        >
          📎 Kies Backup Bestand
        </button>
      </div>

      {/* Selected Backup Info */}
      {selectedBackup && (
        <div style={{
          background: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '20px',
          marginBottom: '30px'
        }}>
          <h4 style={{ margin: '0 0 15px 0', color: '#1f2937' }}>
            📋 Geselecteerd Backup Bestand
          </h4>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '15px',
            fontSize: '14px',
            marginBottom: '20px'
          }}>
            <div>
              <strong>📁 Bestand:</strong> {selectedBackup.filename}<br/>
              <strong>📅 Datum:</strong> {new Date(selectedBackup.stats.backupDate).toLocaleString()}<br/>
              <strong>🏷️ Versie:</strong> {selectedBackup.stats.version}
            </div>
            <div>
              <strong>📊 Collections:</strong> {selectedBackup.stats.collections.length}<br/>
              <strong>📄 Documenten:</strong> {selectedBackup.stats.totalDocuments}<br/>
              <strong>💾 Grootte:</strong> {(JSON.stringify(selectedBackup.data).length / 1024 / 1024).toFixed(2)} MB
            </div>
          </div>

          {/* Restore Preview */}
          {restorePreview && restorePreview.success && (
            <div style={{
              background: '#f0f9ff',
              border: '1px solid #3b82f6',
              borderRadius: '6px',
              padding: '15px',
              marginBottom: '20px'
            }}>
              <h5 style={{ margin: '0 0 10px 0', color: '#1e40af' }}>
                🔍 Restore Preview - Wat gaat er gebeuren?
              </h5>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '10px',
                fontSize: '13px',
                color: '#1e40af'
              }}>
                <div>
                  <strong>📝 Totale wijzigingen:</strong> {restorePreview.summary.totalChanges}
                </div>
                <div>
                  <strong>📁 Collections:</strong> {restorePreview.summary.collectionsAffected}
                </div>
                <div>
                  <strong>➕ Toevoegen:</strong> {Object.values(restorePreview.changes.documentsToAdd).reduce((sum, arr) => sum + arr.length, 0)}
                </div>
                <div>
                  <strong>📝 Bijwerken:</strong> {Object.values(restorePreview.changes.documentsToUpdate).reduce((sum, arr) => sum + arr.length, 0)}
                </div>
              </div>
            </div>
          )}

          {/* Restore Actions */}
          <div style={{
            display: 'flex',
            gap: '15px',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={() => handleRestore(true)}
              disabled={isRestoring}
              style={{
                background: isRestoring ? '#9ca3af' : '#ef4444',
                color: 'white',
                border: 'none',
                padding: '15px 25px',
                borderRadius: '8px',
                cursor: isRestoring ? 'not-allowed' : 'pointer',
                fontWeight: '600',
                fontSize: '14px'
              }}
            >
              {isRestoring ? '⏳ Bezig...' : '🚨 VOLLEDIGE RESTORE (Overschrijf Alles)'}
            </button>
            
            <button
              onClick={() => handleRestore(false)}
              disabled={isRestoring}
              style={{
                background: isRestoring ? '#9ca3af' : '#f59e0b',
                color: 'white',
                border: 'none',
                padding: '15px 25px',
                borderRadius: '8px',
                cursor: isRestoring ? 'not-allowed' : 'pointer',
                fontWeight: '600',
                fontSize: '14px'
              }}
            >
              {isRestoring ? '⏳ Bezig...' : '📝 MERGE RESTORE (Behoud Bestaand)'}
            </button>
            
            <button
              onClick={() => {
                setSelectedBackup(null);
                setRestorePreview(null);
              }}
              style={{
                background: '#6b7280',
                color: 'white',
                border: 'none',
                padding: '15px 25px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '14px'
              }}
            >
              ❌ Annuleren
            </button>
          </div>
        </div>
      )}
    </div>
  );

  // Inspector Tab
  const renderInspectorTab = () => (
    <div>
      <div style={{
        background: '#f0f9ff',
        border: '1px solid #3b82f6',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '30px'
      }}>
        <h4 style={{ color: '#1e40af', margin: '0 0 15px 0' }}>
          🔍 Backup Inspector & Validator
        </h4>
        <p style={{ color: '#1e40af', margin: '0 0 15px 0', fontSize: '14px' }}>
          Upload een backup bestand om de complete structuur en inhoud te analyseren. Perfect om te controleren of je backup alles bevat wat je nodig hebt.
        </p>
        <ul style={{ color: '#1e40af', margin: 0, paddingLeft: '20px', fontSize: '13px' }}>
          <li>Automatische validatie van alle database collections</li>
          <li>Overzicht van documenten per collection + subcollections</li>
          <li>Data integriteit check (werkvormen format, package info, etc.)</li>
          <li>Aanbevelingen voor data kwaliteit verbetering</li>
        </ul>
      </div>

      {/* File Upload for Inspector */}
      <div style={{
        border: '2px dashed #d1d5db',
        borderRadius: '8px',
        padding: '40px',
        textAlign: 'center',
        marginBottom: '30px',
        background: '#f9fafb'
      }}>
        <input
          ref={inspectorFileRef}
          type="file"
          accept=".json"
          onChange={handleInspectorUpload}
          style={{ display: 'none' }}
        />
        
        <div style={{ fontSize: '48px', marginBottom: '15px' }}>🔬</div>
        
        <h4 style={{ margin: '0 0 10px 0', color: '#1f2937' }}>
          Analyseer Backup Bestand
        </h4>
        
        <p style={{ color: '#6b7280', margin: '0 0 20px 0', fontSize: '14px' }}>
          Upload een backup JSON bestand voor complete structuur analyse
        </p>
        
        <button
          onClick={() => inspectorFileRef.current?.click()}
          disabled={inspectorLoading}
          style={{
            background: inspectorLoading ? '#9ca3af' : '#3b82f6',
            color: 'white',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '6px',
            cursor: inspectorLoading ? 'not-allowed' : 'pointer',
            fontWeight: '600'
          }}
        >
          {inspectorLoading ? '⏳ Analyseren...' : '🔬 Analyseer Backup'}
        </button>
      </div>

      {/* Inspector Results */}
      {inspectorData && (
        <div style={{
          background: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '0',
          marginBottom: '30px',
          overflow: 'hidden'
        }}>
          {/* Inspector Header */}
          <div style={{
            background: '#f8fafc',
            padding: '20px',
            borderBottom: '1px solid #e5e7eb'
          }}>
            <h4 style={{ margin: '0 0 10px 0', color: '#1f2937', display: 'flex', alignItems: 'center', gap: '10px' }}>
              📊 Backup Analyse: {inspectorData.filename}
            </h4>
            <div style={{ display: 'flex', gap: '30px', fontSize: '14px', color: '#6b7280' }}>
              <div><strong>Backup Datum:</strong> {new Date(inspectorData.stats.backupDate).toLocaleString()}</div>
              <div><strong>Collections:</strong> {inspectorData.analysis.totalCollections}</div>
              <div><strong>Documenten:</strong> {inspectorData.analysis.totalDocuments}</div>
              <div><strong>Grootte:</strong> {(JSON.stringify(inspectorData.backupData).length / 1024 / 1024).toFixed(2)} MB</div>
            </div>
          </div>

          {/* Recommendations */}
          {inspectorData.analysis.recommendations.length > 0 && (
            <div style={{ padding: '20px', borderBottom: '1px solid #e5e7eb' }}>
              <h5 style={{ margin: '0 0 15px 0', color: '#1f2937' }}>💡 Aanbevelingen</h5>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {inspectorData.analysis.recommendations.map((rec, index) => (
                  <div key={index} style={{
                    padding: '10px',
                    borderRadius: '6px',
                    fontSize: '14px',
                    background: rec.type === 'warning' ? '#fef3c7' : rec.type === 'success' ? '#ecfdf5' : '#f0f9ff',
                    color: rec.type === 'warning' ? '#92400e' : rec.type === 'success' ? '#166534' : '#1e40af',
                    border: `1px solid ${rec.type === 'warning' ? '#f59e0b' : rec.type === 'success' ? '#10b981' : '#3b82f6'}`
                  }}>
                    {rec.type === 'warning' && '⚠️ '}
                    {rec.type === 'success' && '✅ '}
                    {rec.type === 'info' && 'ℹ️ '}
                    {rec.message}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Collections Analysis */}
          <div style={{ padding: '20px' }}>
            <h5 style={{ margin: '0 0 20px 0', color: '#1f2937' }}>📁 Collections Overzicht</h5>
            
            <div style={{ display: 'grid', gap: '20px' }}>
              {Object.entries(inspectorData.analysis.collections).map(([collectionName, analysis]) => (
                <div key={collectionName} style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '15px',
                  background: '#fafbfc'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '15px'
                  }}>
                    <h6 style={{ margin: 0, color: '#1f2937', fontSize: '1rem' }}>
                      📂 {collectionName}
                    </h6>
                    <div style={{ fontSize: '14px', color: '#6b7280', textAlign: 'right' }}>
                      <div>{analysis.documentCount} documenten</div>
                      {Object.keys(analysis.subcollections).length > 0 && (
                        <div style={{ fontSize: '12px' }}>
                          + subcollections: {Object.entries(analysis.subcollections).map(([name, count]) => `${name}(${count})`).join(', ')}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Fields */}
                  <div style={{ marginBottom: '15px' }}>
                    <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '5px' }}>
                      <strong>Velden ({Array.from(analysis.fields).length}):</strong>
                    </div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>
                      {Array.from(analysis.fields).slice(0, 10).join(', ')}
                      {Array.from(analysis.fields).length > 10 && ` ... +${Array.from(analysis.fields).length - 10} meer`}
                    </div>
                  </div>

                  {/* Sample Data */}
                  {analysis.sampleData.length > 0 && (
                    <div style={{ marginBottom: '15px' }}>
                      <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '5px' }}>
                        <strong>Voorbeelden:</strong>
                      </div>
                      {analysis.sampleData.map((sample, index) => (
                        <div key={index} style={{ 
                          fontSize: '12px', 
                          color: '#6b7280', 
                          marginBottom: '3px',
                          fontFamily: 'monospace',
                          background: '#f1f5f9',
                          padding: '4px 8px',
                          borderRadius: '4px'
                        }}>
                          {sample.id}: {JSON.stringify(sample.preview)}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Issues */}
                  {analysis.issues.length > 0 && (
                    <div>
                      <div style={{ fontSize: '13px', color: '#ef4444', marginBottom: '5px' }}>
                        <strong>⚠️ Aandachtspunten:</strong>
                      </div>
                      {analysis.issues.map((issue, index) => (
                        <div key={index} style={{ 
                          fontSize: '12px', 
                          color: '#ef4444',
                          marginBottom: '2px'
                        }}>
                          • {issue}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div style={{
            background: '#f8fafc',
            padding: '20px',
            borderTop: '1px solid #e5e7eb',
            display: 'flex',
            gap: '15px',
            justifyContent: 'center'
          }}>
            <button
              onClick={() => {
                setSelectedBackup({
                  filename: inspectorData.filename,
                  data: inspectorData.backupData,
                  stats: inspectorData.stats
                });
                setActiveTab('restore');
              }}
              style={{
                background: '#f59e0b',
                color: 'white',
                border: 'none',
                padding: '12px 20px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '14px'
              }}
            >
              🔄 Gebruik voor Restore
            </button>
            
            <button
              onClick={() => downloadBackup(inspectorData.backupData, `analyzed-${inspectorData.filename}`)}
              style={{
                background: '#3b82f6',
                color: 'white',
                border: 'none',
                padding: '12px 20px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '14px'
              }}
            >
              📥 Download Backup
            </button>
            
            <button
              onClick={() => {
                setInspectorData(null);
              }}
              style={{
                background: '#6b7280',
                color: 'white',
                border: 'none',
                padding: '12px 20px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '14px'
              }}
            >
              ❌ Sluiten
            </button>
          </div>
        </div>
      )}
    </div>
  );
  const renderCompareTab = () => (
    <div>
      <div style={{
        background: '#f0f9ff',
        border: '1px solid #3b82f6',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '30px'
      }}>
        <h4 style={{ color: '#1e40af', margin: '0 0 15px 0' }}>
          🔍 Backup Vergelijking
        </h4>
        <p style={{ color: '#1e40af', margin: '0', fontSize: '14px' }}>
          Vergelijk verschillende backup bestanden om wijzigingen tussen verschillende momenten te zien.
        </p>
      </div>

      <div style={{
        textAlign: 'center',
        padding: '60px 20px',
        color: '#6b7280'
      }}>
        <div style={{ fontSize: '4rem', marginBottom: '20px' }}>🔍</div>
        <h3 style={{ marginBottom: '10px' }}>Backup Vergelijking</h3>
        <p style={{ margin: 0, fontSize: '14px' }}>
          Upload twee backup bestanden om verschillen te vergelijken.<br/>
          Deze functionaliteit wordt in een volgende update toegevoegd.
        </p>
      </div>
    </div>
  );

  // History Tab
  const renderHistoryTab = () => (
    <div>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '30px'
      }}>
        <h4 style={{ margin: 0, color: '#1f2937' }}>
          📚 Backup Geschiedenis
        </h4>
        
        <div style={{ fontSize: '14px', color: '#6b7280' }}>
          {backupHistory.length} backups beschikbaar
        </div>
      </div>

      {backupHistory.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          color: '#6b7280',
          background: '#f9fafb',
          borderRadius: '8px',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{ fontSize: '4rem', marginBottom: '20px' }}>📚</div>
          <h3 style={{ marginBottom: '10px' }}>Nog geen backup geschiedenis</h3>
          <p style={{ margin: 0, fontSize: '14px' }}>
            Maak je eerste backup om de geschiedenis te starten.<br/>
            Alle backups die je maakt verschijnen hier voor eenvoudig beheer.
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '15px' }}>
          {backupHistory.map((backup) => (
            <div
              key={backup.id}
              style={{
                background: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '20px'
              }}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '15px'
              }}>
                <div>
                  <h5 style={{ margin: '0 0 5px 0', color: '#1f2937' }}>
                    📁 {backup.filename}
                  </h5>
                  <div style={{ fontSize: '13px', color: '#6b7280' }}>
                    {new Date(backup.timestamp).toLocaleString()}
                  </div>
                </div>
                
                <div style={{ textAlign: 'right', fontSize: '13px', color: '#6b7280' }}>
                  <div>{backup.stats.totalDocuments} documenten</div>
                  <div>{(backup.stats.backupSize / 1024 / 1024).toFixed(2)} MB</div>
                </div>
              </div>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: '10px',
                fontSize: '12px',
                color: '#6b7280',
                marginBottom: '15px'
              }}>
                <div><strong>Collections:</strong> {backup.stats.totalCollections}</div>
                <div><strong>Documenten:</strong> {backup.stats.totalDocuments}</div>
                <div><strong>Grootte:</strong> {(backup.stats.backupSize / 1024 / 1024).toFixed(2)} MB</div>
              </div>
              
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => downloadBackup(backup.data, `re-download-${backup.filename}`)}
                  style={{
                    background: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                >
                  📥 Download
                </button>
                
                <button
                  onClick={() => {
                    setSelectedBackup({
                      filename: backup.filename,
                      data: backup.data,
                      stats: getBackupStats(backup.data)
                    });
                    setActiveTab('restore');
                  }}
                  style={{
                    background: '#f59e0b',
                    color: 'white',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                >
                  🔄 Herstel
                </button>
                
                <button
                  onClick={() => {
                    // eslint-disable-next-line no-restricted-globals
                    if (confirm(`Weet je zeker dat je backup "${backup.filename}" wilt verwijderen uit de geschiedenis?`)) {
                      setBackupHistory(prev => prev.filter(b => b.id !== backup.id));
                    }
                  }}
                  style={{
                    background: '#ef4444',
                    color: 'white',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                >
                  🗑️ Verwijder
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // Main render
  return (
    <div style={{
      background: 'white',
      borderRadius: '12px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
      padding: '30px',
      maxWidth: '100%'
    }}>
      <h2 style={{ marginBottom: '30px', color: '#1f2937' }}>
        💾 Master Database Backup Systeem
      </h2>

      {renderTabNavigation()}

      {activeTab === 'create' && renderCreateTab()}
      {activeTab === 'restore' && renderRestoreTab()}
      {activeTab === 'inspector' && renderInspectorTab()}
      {activeTab === 'compare' && renderCompareTab()}
      {activeTab === 'history' && renderHistoryTab()}

      {/* Footer Info */}
      <div style={{
        marginTop: '40px',
        padding: '20px',
        background: '#f9fafb',
        borderRadius: '8px',
        fontSize: '12px',
        color: '#6b7280',
        textAlign: 'center'
      }}>
        <div style={{ marginBottom: '10px' }}>
          <strong>💡 Pro Tip:</strong> Maak dagelijks een backup voordat je belangrijke wijzigingen doorvoert
        </div>
        <div>
          <strong>⚠️ Belangrijke herinnering:</strong> Test restore functionaliteit eerst met een kopie van je productie data
        </div>
      </div>
    </div>
  );
};

export default MasterBackup;